import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

class ChatbotService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('adminToken');
          sessionStorage.removeItem('adminToken');
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Chatbot Intent Management
  async getIntents() {
    try {
      const response = await this.api.get('/chatbot/intents');
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get intents');
      }
    } catch (error) {
      console.error('Error getting intents:', error);
      throw error;
    }
  }

  async createIntent(intentData) {
    try {
      const response = await this.api.post('/chatbot/intents', intentData);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create intent');
      }
    } catch (error) {
      console.error('Error creating intent:', error);
      throw error;
    }
  }

  async updateIntent(id, intentData) {
    try {
      const response = await this.api.put(`/chatbot/intents/${id}`, intentData);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update intent');
      }
    } catch (error) {
      console.error('Error updating intent:', error);
      throw error;
    }
  }

  async deleteIntent(id) {
    try {
      const response = await this.api.delete(`/chatbot/intents/${id}`);
      
      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to delete intent');
      }
    } catch (error) {
      console.error('Error deleting intent:', error);
      throw error;
    }
  }

  async getChatLogs(limit = 100, offset = 0) {
    try {
      const response = await this.api.get(`/chatbot/logs?limit=${limit}&offset=${offset}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get chat logs');
      }
    } catch (error) {
      console.error('Error getting chat logs:', error);
      throw error;
    }
  }

  // Public chatbot endpoint
  async sendMessage(message) {
    try {
      const response = await this.api.post('/chatbot/message', { message });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }
}

export const chatbotService = new ChatbotService();
export default chatbotService;
